import qs from 'qs';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import SwiperClass from 'swiper/types/swiper-class';
import isEqual from 'lodash/isEqual';

import http from '../helpers/http';
import { device } from '../helpers/device';

import User from './models/User';
import Court from './models/Court';

type Slider = {
    cache: any,

    swiper: SwiperClass | null,

    id: string,
    mode: 'users' | 'courts',
    query: any & {
        page: number,
        size: number,
    }

    index: number,
    items: any[], // (User | Court)[],
    pages: number,
    total: number
    selected: any, //  User | Court | null,
};

type CarouselSlider = {
    prev: Slider;
    next: Slider;
    curr: Slider;
};

type CarouselMenu = {
    open: boolean,
    mode: string,
    type: string,

    filter: {
        gender: string[],
        rating: {
            active: string[],

            age: number[],
            experience: number[],
            ntrp: number[],
            ello: number[],
        },

        calendar: {
            type: '' | 'schedule' | 'specific',
            reset?: boolean,

            schedule: { date: string, time: string[] }[],
            specific: { date: string, time: string[] }[],
        },

        preference: {
            court: string[],
            play: string[],
            training: string[],
        },
    },

    sort: {
        property: string,
        order: number,
    },
};

export default class CarouselStore {
    isProcess = false;

    /* */

    type = 'search';

    slider: CarouselSlider = {
        prev: {
            cache: {},

            swiper: null,

            id: '',
            mode: 'users',
            query: {
                page: 1,
                size: 10,
            },

            index: 0,
            items: [],
            pages: 0,
            total: 0,
            selected: null,
        },

        next: {
            cache: {},

            swiper: null,

            id: '',
            mode: 'users',
            query: {
                page: 1,
                size: 10,
            },

            index: 0,
            items: [],
            pages: 0,
            total: 0,
            selected: null,
        },

        curr: {
            cache: {},

            swiper: null,

            id: '',
            mode: 'users',
            query: {
                page: 1,
                size: 10,
            },

            index: 0,
            items: [],
            pages: 0,
            total: 0,
            selected: null,
        },
    };

    menu: CarouselMenu = {
        open: false,
        mode: '',
        type: '',

        filter: {
            gender: ['male', 'female'],
            rating: {
                active: ['age', 'ntrp'],

                age: [10, 70],
                experience: [0, 30],
                ntrp: [1, 7],
                ello: [1000, 3000],
            },

            calendar: {
                type: 'specific',

                schedule: [],
                specific: [],
            },

            preference: {
                court: ['hard', 'clay', 'grass'],
                play: ['single', 'double', 'tournament'],
                training: ['individual', 'group', 'professional'],
            },
        },

        sort: {
            property: '',
            order: 0,
        },
    };

    stats = {
        isProcess: false,

        search: {
            isLoaded: false,

            players: 0,
            coaches: 0,
            courts: 0,
        },
        courts: {
            isLoaded: false,

            players: 0,
            coaches: 0,
            courts: 0,
        },
        users: {
            isLoaded: false,

            users: 0,
            courts: 0,
        },
    };

    history: any[] = [];
    lastPoppedSnapshot: any = null;
    isBackNavigation = false;
    returnIndex: number | null = null;
    returnSelected: any = null;
    userHistory: string[] = [];
    courtOrigin: { id: string, role: string, index: number, image: string | null } | null = null;
    backgroundCourtImage: string | null = null;
    searchMode = 'players';

    preloadedSearchCourts: { items: Court[], total: number, pages: number } | null = null;
    preloadedSearchUsers: { items: User[], total: number, pages: number, role: string } | null = null;

    /* */

    constructor() {
        makeAutoObservable(this);
    }

    /* */

    slideToByType(type: 'prev' | 'next' | 'curr', index?: number) {
        const slider = this.slider[type];

        if (index == null) {
            index = (slider.mode === 'users' && device.isWideScreen ? 2 : 1) + (type === 'curr' ? 1 : 0);
        }

        if (slider.swiper) {
            if (slider.swiper.activeIndex === index) {
                slider.swiper.emit('slideChange');
            } else {
                slider.swiper.slideTo(index, 0);
            }
        }
    }

    /* */

    async loadSearch(mode: 'users' | 'courts', query: any = {}, onReady?: () => void) {
        this.type = 'search';
        this.userHistory = [];
        this.courtOrigin = null;
        this.backgroundCourtImage = null;
        this.searchMode = mode === 'courts' ? 'courts' : (query.where?.role || 'players');

        let needsReset = false;
        let savedReturnIndex: number | null = null;
        if (mode !== this.slider.curr.mode
            || !isEqual(query.where, this.slider.curr.query.where)
            || !isEqual(query.sort, this.slider.curr.query.sort)) {
            needsReset = true;
            savedReturnIndex = this.returnIndex;
            this.returnIndex = null;

            query.page = 1;

            if (!this.isBackNavigation) {
                this.history.push(toJS(this.slider));
            }
        }
        this.isBackNavigation = false;

        if (needsReset || !this.stats.search.isLoaded) {
            const { data: stats } = await http.get('/v1/search/stats');

            runInAction(() => {
                this.stats.search.isLoaded = true;

                this.stats.search.players = stats.players;
                this.stats.search.coaches = stats.coaches;
                this.stats.search.courts = stats.courts;
            });
        }

        runInAction(() => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const length = mode === 'users' ? this.stats.search[query.where.role] : this.stats.search.courts;

            this.slider.curr.mode = mode;
            this.slider.curr.query = {
                ...toJS(this.slider.curr.query),
                ...query,
            };
            const swiper1 = this.slider.curr.swiper;
            if (needsReset) { swiper1?.$wrapperEl?.removeClass('slider-init'); }
            if (needsReset) {
                const prevItems = this.slider.prev.items.filter((i: any) => !i.isLoading);
                this.slider.curr.items = (savedReturnIndex != null && prevItems.length > 0)
                    ? prevItems
                    : Array.from({ length }, () => ({
                        isLoading: true,

                        id: (Math.random() * 100000).toString(16).replace('.', ''),
                    }));
            } else {
                this.slider.curr.items = this.slider.curr.items.filter(item => !item.isLoading);
                if (this.slider.curr.items.length < length) {
                    this.slider.curr.items = this.slider.curr.items.concat(
                        Array.from({ length: length - this.slider.curr.items.length }, () => ({
                            isLoading: true,

                            id: (Math.random() * 100000).toString(16).replace('.', ''),
                        })),
                    );
                }
            }

            if (needsReset) {
                const target = savedReturnIndex != null ? savedReturnIndex + 1 : undefined;
                this.slideToByType('curr', target);
                process.nextTick(() => {
                    this.slideToByType('curr', target);
                    if (onReady) { onReady(); }
                });
            } else if (onReady) {
                onReady();
            }

            setTimeout(() => swiper1?.$wrapperEl?.addClass('slider-init'), 20);
        });

        const { data } = await http.get(`/v1/search/${mode}?${qs.stringify(query)}`);

        runInAction(() => {
            const Model = {
                users: User,
                courts: Court,
            };

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const length = mode === 'users' ? this.stats.search[query.where.role] : this.stats.search.courts;

            this.slider.curr.mode = mode;
            this.slider.curr.query = {
                ...toJS(this.slider.curr.query),
                ...query,
            };
            const swiper2 = this.slider.curr.swiper;
            if (needsReset) { swiper2?.$wrapperEl?.removeClass('slider-init'); }
            if (needsReset && savedReturnIndex != null) {
                const freshItems = data.items.map((d: any) => new Model[mode]().fromJSON(d));
                const freshIds = new Set(freshItems.map((i: any) => i.id));
                const beyondPageItems = this.slider.curr.items.filter((item: any) => !item.isLoading && !freshIds.has(item.id));
                this.slider.curr.items = freshItems
                    .concat(beyondPageItems)
                    .filter((item: any, i: number, items: any[]) => items.findIndex((d: any) => d.id === item.id) === i);
            } else {
                this.slider.curr.items = (needsReset ? [] : this.slider.curr.items.filter(item => !item.isLoading))
                    .concat(data.items.map((d: any) => new Model[mode]().fromJSON(d)))
                    .filter((item: any, i, items) => items.findIndex((d: any) => d.id === item.id) === i);
            }
            if (this.slider.curr.items.length < length && data.total > length) {
                this.slider.curr.items = this.slider.curr.items.concat(
                    Array.from({ length: length - this.slider.curr.items.length }, () => ({
                        isLoading: true,

                        id: (Math.random() * 100000).toString(16).replace('.', ''),
                    })),
                );
            }
            this.slider.curr.pages = data.pages;
            this.slider.curr.total = data.total;

            const target = savedReturnIndex != null ? savedReturnIndex + 1 : this.slider.curr.index + 1;
            this.slideToByType('curr', target);
            if (needsReset) {
                process.nextTick(() => this.slideToByType('curr', target));
            }

            setTimeout(() => swiper2?.$wrapperEl?.addClass('slider-init'), 20);
        });
    }

    async loadCourts(id: string, mode: 'users' | 'courts', query: any = {}, onReady?: () => void) {
        this.type = 'courts';
        this.userHistory = [];
        this.courtOrigin = null;
        this.returnSelected = null;
        const wasBackNavigation = this.isBackNavigation;
        this.isBackNavigation = false;
        if (!wasBackNavigation) {
            this.backgroundCourtImage = null;
        }

        let needsReset = false;
        let savedReturnIndex: number | null = null;
        if (id !== this.slider.curr.id || mode !== this.slider.curr.mode
            || !isEqual(query.where, this.slider.curr.query.where)
            || !isEqual(query.sort, this.slider.curr.query.sort)) {
            needsReset = true;

            query.page = 1;

            if (wasBackNavigation) {
                savedReturnIndex = this.returnIndex;
                this.returnIndex = null;
            } else {
                this.history.push(toJS(this.slider));
            }
        }

        let needsChange = needsReset && !wasBackNavigation;
        if (wasBackNavigation) {
            const snapshot = this.lastPoppedSnapshot;
            this.lastPoppedSnapshot = null;
            const restored = ((snapshot?.curr?.items as any[]) || []).filter((i: any) => !i.isLoading);
            if (restored.length > 0) {
                runInAction(() => {
                    const swiper0 = this.slider.curr.swiper;
                    swiper0?.$wrapperEl?.removeClass('slider-init');
                    this.slider.curr.mode = mode;
                    this.slider.curr.items = restored;
                    this.slideToByType('curr', savedReturnIndex != null ? savedReturnIndex + 1 : undefined);
                    setTimeout(() => swiper0?.$wrapperEl?.addClass('slider-init'), 20);
                });
            }
        }
        let courtStableTarget: number | undefined = undefined;
        if (needsReset) {
            runInAction(() => {
                if (mode === 'users' && this.slider.curr.mode === 'courts' && this.slider.curr.selected
                    && this.slider.curr.selected.id === id) {
                    const court = this.slider.curr.selected;

                    this.slider.prev.id = court.id;
                    this.slider.prev.mode = 'courts';
                    this.slider.prev.index = 0;
                    this.slider.prev.selected = court;
                    this.slider.prev.items = [this.slider.prev.selected];

                    this.slider.curr.id = court.id;
                    this.slider.curr.mode = mode;
                    this.slider.curr.query = {
                        ...toJS(this.slider.curr.query),
                        ...query,
                    };
                    const swiper1 = this.slider.curr.swiper;
                    swiper1?.$wrapperEl?.removeClass('slider-init');
                    this.slider.curr.items = [...court.players]
                        .sort((a: any, b: any) => Number(b.totalGames || 0) - Number(a.totalGames || 0));

                    this.slideToByType('curr');
                    setTimeout(() => swiper1?.$wrapperEl?.addClass('slider-init'), 20);
                    needsChange = false;
                }
            });

            process.nextTick(() => {
                this.slideToByType('curr', savedReturnIndex != null ? savedReturnIndex + 1 : undefined);
                if (onReady) { onReady(); }
            });
        }

        if (needsReset || !this.stats.courts.isLoaded) {
            const { data: stats } = await http.get(`/v1/courts/${id}/stats`);

            runInAction(() => {
                this.stats.courts.isLoaded = true;

                this.stats.courts.players = stats.players;
                this.stats.courts.coaches = stats.coaches;
                this.stats.courts.courts = stats.courts;
            });
        }

        runInAction(() => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const length = mode === 'users' ? this.stats.courts[query.where.role] : this.stats.courts.courts;

            this.slider.curr.mode = mode;
            this.slider.curr.query = {
                ...toJS(this.slider.curr.query),
                ...query,
            };
            const swiper2 = this.slider.curr.swiper;
            if (needsChange) { swiper2?.$wrapperEl?.removeClass('slider-init'); }
            if (needsChange) {
                this.slider.curr.items = Array.from({ length }, () => ({
                    isLoading: true,

                    id: (Math.random() * 100000).toString(16).replace('.', ''),
                }));
            } else {
                courtStableTarget = savedReturnIndex != null
                    ? savedReturnIndex + 1
                    : (this.slider.curr.swiper?.activeIndex ?? (this.slider.curr.index + 1));
                this.slider.curr.items = this.slider.curr.items.filter(item => !item.isLoading);
                if (this.slider.curr.items.length < length) {
                    this.slider.curr.items = this.slider.curr.items.concat(
                        Array.from({ length: length - this.slider.curr.items.length }, () => ({
                            isLoading: true,

                            id: (Math.random() * 100000).toString(16).replace('.', ''),
                        })),
                    );
                }
                this.slideToByType('curr', courtStableTarget);
            }

            if (needsChange) {
                const initTarget = savedReturnIndex != null ? savedReturnIndex + 1 : undefined;
                this.slideToByType('curr', initTarget);
                process.nextTick(() => this.slideToByType('curr', initTarget));
            } else {
                process.nextTick(() => this.slideToByType('curr', courtStableTarget));
            }

            setTimeout(() => swiper2?.$wrapperEl?.addClass('slider-init'), 20);
        });

        const { data } = await http.get(`/v1/courts/${id}/${mode}?${qs.stringify(query)}`);

        runInAction(() => {
            const Model = {
                users: User,
                courts: Court,
            };

            //

            const court = new Court().fromJSON(data.current);

            //

            this.slider.prev.id = court.id;
            this.slider.prev.mode = 'courts';
            this.slider.prev.query = {
                ...toJS(this.slider.curr.query),
                ...query,
            };
            this.slider.prev.index = 0;
            this.slider.prev.selected = court; // (this.slider.curr.mode === 'courts' && this.slider.curr.selected) ? this.slider.curr.selected : court;
            this.slider.prev.items = [this.slider.prev.selected];
            this.slider.prev.pages = 1;
            this.slider.prev.total = 1;
            this.backgroundCourtImage = null;

            //

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const length = mode === 'users' ? this.stats.courts[query.where.role] : this.stats.courts.courts;

            this.slider.curr.id = id;
            this.slider.curr.mode = mode;
            this.slider.curr.query = {
                ...toJS(this.slider.curr.query),
                ...query,
            };
            const swiper3 = this.slider.curr.swiper;
            if (needsChange) { swiper3?.$wrapperEl?.removeClass('slider-init'); }
            const freshItems = data.items.map((d: any) => new Model[mode]().fromJSON(d)) as any[];
            const existingItems = needsChange ? [] : this.slider.curr.items.filter((item: any) => !item.isLoading);
            this.slider.curr.items = (query.page > 1 ? [...existingItems, ...freshItems] : [...freshItems, ...existingItems])
                .filter((item: any, i: number, arr: any[]) => arr.findIndex((d: any) => d.id === item.id) === i);
            if (this.slider.curr.items.length < length && data.total > length) {
                this.slider.curr.items = this.slider.curr.items.concat(
                    Array.from({ length: length - this.slider.curr.items.length }, () => ({
                        isLoading: true,

                        id: (Math.random() * 100000).toString(16).replace('.', ''),
                    })),
                );
            }
            this.slider.curr.pages = data.pages;
            this.slider.curr.total = data.total;

            const courtTarget = needsChange
                ? (savedReturnIndex != null ? savedReturnIndex + 1 : undefined)
                : courtStableTarget;
            this.slideToByType('curr', courtTarget);

            if (needsChange) {
                process.nextTick(() => this.slideToByType('curr', savedReturnIndex != null ? savedReturnIndex + 1 : undefined));
            } else {
                process.nextTick(() => this.slideToByType('curr', courtTarget));
            }

            setTimeout(() => swiper3?.$wrapperEl?.addClass('slider-init'), 20);
        });

        // Preload search courts so the prev slider has side elements ready for no-history swipe-down
        try {
            const { data: searchData } = await http.get(`/v1/search/courts?${qs.stringify({ page: 1, size: 10, sort: { totalPlayers: -1 } })}`);
            runInAction(() => {
                if (this.type === 'courts') {
                    this.preloadedSearchCourts = {
                        items: searchData.items.map((d: any) => new Court().fromJSON(d)),
                        total: searchData.total,
                        pages: searchData.pages,
                    };
                }
            });
        } catch {
            // Silently ignore
        }
    }

    async loadUsers(id: string, mode: 'users' | 'courts', query: any = {}, onReady?: () => void) {
        const prevType = this.type;
        this.type = 'users';

        let needsReset = false;
        if (id !== this.slider.curr.id || mode !== this.slider.curr.mode
            || !isEqual(query.where, this.slider.curr.query.where)
            || !isEqual(query.sort, this.slider.curr.query.sort)) {
            needsReset = true;

            query.page = 1;

            // Tab switches (same user, different mode) must not pollute navigation history.
            // Only push when actually navigating to/from a different page.
            const isTabSwitch = prevType === 'users' && id === this.slider.curr.id;
            if (!this.isBackNavigation && !isTabSwitch) {
                if (prevType === 'courts' && this.courtOrigin === null) {
                    this.courtOrigin = {
                        id: this.slider.curr.id,
                        role: this.slider.curr.query?.where?.role || 'players',
                        index: this.slider.curr.index,
                        image: this.slider.prev.selected?.image || null,
                    };
                    this.backgroundCourtImage = this.courtOrigin.image;
                } else if (prevType === 'users') {
                    this.backgroundCourtImage = null;
                }
                this.history.push(toJS(this.slider));
                this.userHistory.push(id);
            }
        }
        const wasBackNavigation = this.isBackNavigation;
        this.isBackNavigation = false;

        let onReadyTarget: number | undefined;
        let usersStableTarget: number | undefined = undefined;
        let needsChange = needsReset && !wasBackNavigation;
        if (needsReset) {
            runInAction(() => {
                if (!wasBackNavigation && mode === 'users' && this.slider.curr.mode === 'users' && this.slider.curr.selected
                    && this.slider.curr.selected.id === id) {
                    const user = this.slider.curr.selected;

                    this.slider.prev.id = user.id;
                    this.slider.prev.mode = 'users';
                    this.slider.prev.index = 0;
                    this.slider.prev.selected = user;
                    this.slider.prev.items = [this.slider.prev.selected];

                    this.slider.curr.id = user.id;
                    this.slider.curr.mode = mode;
                    this.slider.curr.query = {
                        ...toJS(this.slider.curr.query),
                        ...query,
                    };
                    const swiper1 = this.slider.curr.swiper;
                    swiper1?.$wrapperEl?.removeClass('slider-init');
                    this.slider.curr.items = [...user.friends]
                        .sort((a: any, b: any) => Number(b.totalGames || 0) - Number(a.totalGames || 0));

                    this.slideToByType('curr');
                    setTimeout(() => swiper1?.$wrapperEl?.addClass('slider-init'), 20);
                    onReadyTarget = undefined;
                    needsChange = false;
                } else if (wasBackNavigation) {
                    const snapshot = this.lastPoppedSnapshot;
                    this.lastPoppedSnapshot = null;
                    const restored = ((snapshot?.curr?.items as any[]) || []).filter((i: any) => !i.isLoading);
                    if (restored.length > 0) {
                        const swiper1 = this.slider.curr.swiper;
                        swiper1?.$wrapperEl?.removeClass('slider-init');
                        this.slider.curr.mode = mode;
                        this.slider.curr.items = restored;
                        onReadyTarget = this.slider.prev.index + 1;
                        this.slideToByType('curr', onReadyTarget);
                        setTimeout(() => swiper1?.$wrapperEl?.addClass('slider-init'), 20);
                        needsChange = false;
                    }
                    if (this.returnSelected) {
                        this.slider.prev.selected = this.returnSelected;
                        this.slider.prev.items = [this.returnSelected];
                        this.slider.prev.index = 0;
                        this.returnSelected = null;
                    }
                }
            });

            process.nextTick(() => {
                this.slideToByType('curr', onReadyTarget);
                if (onReady) { onReady(); }
            });
        }

        if (needsReset || !this.stats.users.isLoaded) {
            const { data: stats } = await http.get(`/v1/users/${id}/stats`);

            runInAction(() => {
                this.stats.users.isLoaded = true;

                this.stats.users.users = stats.users;
                this.stats.users.courts = stats.courts;
            });
        }

        runInAction(() => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const length = mode === 'users' ? this.stats.users.users : this.stats.users.courts;

            this.slider.curr.mode = mode;
            this.slider.curr.query = {
                ...toJS(this.slider.curr.query),
                ...query,
            };
            const swiper2 = this.slider.curr.swiper;
            if (needsChange) { swiper2?.$wrapperEl?.removeClass('slider-init'); }
            if (needsChange) {
                this.slider.curr.items = Array.from({ length }, () => ({
                    isLoading: true,

                    id: (Math.random() * 100000).toString(16).replace('.', ''),
                }));
            } else {
                usersStableTarget = (wasBackNavigation || onReadyTarget != null)
                    ? this.slider.curr.index + 1
                    : (this.slider.curr.swiper?.activeIndex ?? (this.slider.curr.index + 1));
                this.slider.curr.items = this.slider.curr.items.filter(item => !item.isLoading);
                if (this.slider.curr.items.length < length) {
                    this.slider.curr.items = this.slider.curr.items.concat(
                        Array.from({ length: length - this.slider.curr.items.length }, () => ({
                            isLoading: true,

                            id: (Math.random() * 100000).toString(16).replace('.', ''),
                        })),
                    );
                }
                this.slideToByType('curr', usersStableTarget);
            }

            if (needsChange) {
                this.slideToByType('curr');
                process.nextTick(() => this.slideToByType('curr'));
            } else {
                process.nextTick(() => this.slideToByType('curr', usersStableTarget));
            }

            setTimeout(() => swiper2?.$wrapperEl?.addClass('slider-init'), 20);
        });

        const { data } = await http.get(`/v1/users/${id}/${mode}?${qs.stringify(query)}`);

        runInAction(() => {
            const Model = {
                users: User,
                courts: Court,
            };

            //

            const user = new User().fromJSON(data.current);

            //

            this.slider.prev.id = user.id;
            this.slider.prev.mode = 'users';
            this.slider.prev.query = {
                ...toJS(this.slider.curr.query),
                ...query,
            };
            this.slider.prev.index = 0;
            this.slider.prev.selected = user; // (this.slider.curr.mode === 'users' && this.slider.curr.selected) ? this.slider.curr.selected : user;
            this.slider.prev.items = [this.slider.prev.selected];
            this.slider.prev.pages = 1;
            this.slider.prev.total = 1;

            //

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const length = this.stats.users[mode];

            this.slider.curr.id = id;
            this.slider.curr.mode = mode;
            this.slider.curr.query = {
                ...toJS(this.slider.curr.query),
                ...query,
            };
            const swiper3 = this.slider.curr.swiper;
            if (needsChange) { swiper3?.$wrapperEl?.removeClass('slider-init'); }
            const freshItems = data.items.map((d: any) => new Model[mode]().fromJSON(d)) as any[];
            const existingItems = needsChange ? [] : this.slider.curr.items.filter((item: any) => !item.isLoading);
            this.slider.curr.items = (query.page > 1 ? [...existingItems, ...freshItems] : [...freshItems, ...existingItems])
                .filter((item: any, i: number, arr: any[]) => arr.findIndex((d: any) => d.id === item.id) === i);
            if (this.slider.curr.items.length < length && data.total > length) {
                this.slider.curr.items = this.slider.curr.items.concat(
                    Array.from({ length: length - this.slider.curr.items.length }, () => ({
                        isLoading: true,

                        id: (Math.random() * 100000).toString(16).replace('.', ''),
                    })),
                );
            }
            this.slider.curr.pages = data.pages;
            this.slider.curr.total = data.total;

            const usersTarget = needsChange ? undefined : usersStableTarget;
            this.slideToByType('curr', usersTarget);

            if (needsChange) {
                process.nextTick(() => this.slideToByType('curr'));
            } else {
                process.nextTick(() => this.slideToByType('curr', usersTarget));
            }

            setTimeout(() => swiper3?.$wrapperEl?.addClass('slider-init'), 20);
        });

        // Preload search users so the prev slider has side elements ready for no-history swipe-down
        try {
            const userRole = (this.slider.prev.selected as any)?.role;
            if (userRole === 'players' || userRole === 'coaches') {
                const { data: searchData } = await http.get(`/v1/search/users?${qs.stringify({ page: 1, size: 10, where: { role: userRole }, sort: { totalGames: -1 } })}`);
                runInAction(() => {
                    if (this.type === 'users') {
                        this.searchMode = userRole;
                        this.preloadedSearchUsers = {
                            items: searchData.items.map((d: any) => new User().fromJSON(d)),
                            total: searchData.total,
                            pages: searchData.pages,
                            role: userRole,
                        };
                    }
                });
            }
        } catch {
            // Silently ignore
        }
    }

    /* */

    popHistory(): string | null {
        if (this.userHistory.length === 0) {
            return null;
        }
        this.userHistory.pop();
        this.lastPoppedSnapshot = this.history.pop();
        this.isBackNavigation = true;
        if (this.courtOrigin !== null && this.userHistory.length === 1) {
            this.backgroundCourtImage = this.courtOrigin.image;
        }
        return this.userHistory[this.userHistory.length - 1] || null;
    }

    /* */

    onChange(type: 'prev' | 'next' | 'curr', index: number) {
        if (type === 'next') {
            runInAction(() => {
                this.slider.next.index = index;
                this.slider.next.selected = this.slider.next.items[index] || null;
            });

            return;
        }

        if (type === 'curr') {
            if (index % 5 === 0 && index > this.slider.curr.index
                && (this.slider.curr.query.page * this.slider.curr.query.size) < this.slider.curr.total) {
                this.slider.curr.query.page += 1;

                if (this.type === 'search') {
                    this.loadSearch(this.slider.curr.mode, toJS(this.slider.curr.query));
                } else if (this.type === 'courts') {
                    this.loadCourts(this.slider.curr.id, this.slider.curr.mode, toJS(this.slider.curr.query));
                } else if (this.type === 'users') {
                    this.loadUsers(this.slider.curr.id, this.slider.curr.mode, toJS(this.slider.curr.query));
                }
            }

            runInAction(() => {
                this.slider.curr.index = index;
                this.slider.curr.selected = this.slider.curr.items[index] || null;

                if (this.slider.curr.selected) {
                    if (this.slider.curr.mode === 'users') {
                        this.slider.next.mode = 'users';
                        this.slider.next.items = [...(this.slider.curr.selected?.friends || [])].sort((a: any, b: any) => Number(b.totalGames || 0) - Number(a.totalGames || 0));
                    } else if (this.slider.curr.mode === 'courts') {
                        this.slider.next.mode = 'users';
                        this.slider.next.items = [...(this.slider.curr.selected?.players || [])].sort((a: any, b: any) => Number(b.totalGames || 0) - Number(a.totalGames || 0));
                    }
                }

                process.nextTick(() => this.slideToByType('next'));
            });

            return;
        }
    }

    /* */

    resetFilter() {
        this.menu.filter = {
            gender: ['male', 'female'],
            rating: {
                active: ['age', 'ntrp'],

                age: [10, 70],
                experience: [0, 30],
                ntrp: [1, 7],
                ello: [1000, 3000],
            },

            calendar: {
                type: 'specific',

                schedule: [],
                specific: [],
            },

            preference: {
                court: ['hard', 'clay', 'grass'],
                play: ['single', 'double', 'tournament'],
                training: ['individual', 'group', 'professional'],
            },
        };
    }

    applyFilter(applyFor: 'search' | 'courts' | 'users') {
        if (applyFor === 'search') {
            this.loadSearch(this.slider.curr.mode, {
                ...this.slider.curr.query,

                where: {
                    ...this.slider.curr.query.where || {},

                    ...this.slider.curr.mode === 'users'
                        ? {
                            filter: {
                                gender: this.menu.filter.gender,

                                ...this.menu.filter.rating.active.includes('age')
                                    ? { age: this.menu.filter.rating.age }
                                    : {},
                                ...this.menu.filter.rating.active.includes('experience')
                                    ? { experience: this.menu.filter.rating.experience }
                                    : {},
                                ...this.menu.filter.rating.active.includes('ntrp')
                                    ? { ntrp: this.menu.filter.rating.ntrp }
                                    : {},
                                ...this.menu.filter.rating.active.includes('ello')
                                    ? { ello: this.menu.filter.rating.ello }
                                    : {},

                                preference: {
                                    court: this.menu.filter.preference.court,

                                    ...this.slider.curr.query?.where?.role === 'players'
                                        ? { play: this.menu.filter.preference.play }
                                        : {},
                                    ...this.slider.curr.query?.where?.role === 'coaches'
                                        ? { training: this.menu.filter.preference.training }
                                        : {},
                                },

                                ...this.menu.filter.calendar.type === 'schedule'
                                    ? { calendar: this.menu.filter.calendar.schedule }
                                    : {},
                                ...this.menu.filter.calendar.type === 'specific'
                                    ? { calendar: this.menu.filter.calendar.specific }
                                    : {},
                            },
                        }
                        : {},

                    ...this.slider.curr.mode === 'courts'
                        ? {
                            filter: {
                                preference: {
                                    court: this.menu.filter.preference.court,
                                },
                            },
                        }
                        : {},
                },

                page: 1,
            });

            return;
        }

        if (applyFor === 'courts') {
            this.loadCourts(this.slider.curr.id, this.slider.curr.mode, {
                ...this.slider.curr.query,

                where: {
                    ...this.slider.curr.query.where || {},

                    ...this.slider.curr.mode === 'users'
                        ? {
                            filter: {
                                gender: this.menu.filter.gender,

                                ...this.menu.filter.rating.active.includes('age')
                                    ? { age: this.menu.filter.rating.age }
                                    : {},
                                ...this.menu.filter.rating.active.includes('experience')
                                    ? { experience: this.menu.filter.rating.experience }
                                    : {},
                                ...this.menu.filter.rating.active.includes('ntrp')
                                    ? { ntrp: this.menu.filter.rating.ntrp }
                                    : {},
                                ...this.menu.filter.rating.active.includes('ello')
                                    ? { ello: this.menu.filter.rating.ello }
                                    : {},

                                preference: {
                                    court: this.menu.filter.preference.court,

                                    ...this.slider.curr.query?.where?.role === 'players'
                                        ? { play: this.menu.filter.preference.play }
                                        : {},
                                    ...this.slider.curr.query?.where?.role === 'coaches'
                                        ? { training: this.menu.filter.preference.training }
                                        : {},
                                },

                                ...this.menu.filter.calendar.type === 'schedule'
                                    ? { calendar: this.menu.filter.calendar.schedule }
                                    : {},
                                ...this.menu.filter.calendar.type === 'specific'
                                    ? { calendar: this.menu.filter.calendar.specific }
                                    : {},
                            },
                        }
                        : {},

                    ...this.slider.curr.mode === 'courts'
                        ? {
                            filter: {
                                preference: {
                                    court: this.menu.filter.preference.court,
                                },
                            },
                        }
                        : {},
                },

                page: 1,
            });

            return;
        }

        if (applyFor === 'users') {
            this.loadUsers(this.slider.curr.id, this.slider.curr.mode, {
                ...this.slider.curr.query,

                where: {
                    ...this.slider.curr.query.where || {},

                    ...this.slider.curr.mode === 'users'
                        ? {
                            filter: {
                                gender: this.menu.filter.gender,

                                ...this.menu.filter.rating.active.includes('age')
                                    ? { age: this.menu.filter.rating.age }
                                    : {},
                                ...this.menu.filter.rating.active.includes('experience')
                                    ? { experience: this.menu.filter.rating.experience }
                                    : {},
                                ...this.menu.filter.rating.active.includes('ntrp')
                                    ? { ntrp: this.menu.filter.rating.ntrp }
                                    : {},
                                ...this.menu.filter.rating.active.includes('ello')
                                    ? { ello: this.menu.filter.rating.ello }
                                    : {},

                                preference: {
                                    court: this.menu.filter.preference.court,

                                    ...this.slider.curr.query?.where?.role === 'players'
                                        ? { play: this.menu.filter.preference.play }
                                        : {},
                                    ...this.slider.curr.query?.where?.role === 'coaches'
                                        ? { training: this.menu.filter.preference.training }
                                        : {},
                                },

                                ...this.menu.filter.calendar.type === 'schedule'
                                    ? { calendar: this.menu.filter.calendar.schedule }
                                    : {},
                                ...this.menu.filter.calendar.type === 'specific'
                                    ? { calendar: this.menu.filter.calendar.specific }
                                    : {},
                            },
                        }
                        : {},

                    ...this.slider.curr.mode === 'courts'
                        ? {
                            filter: {
                                preference: {
                                    court: this.menu.filter.preference.court,
                                },
                            },
                        }
                        : {},
                },

                page: 1,
            });

            return;
        }
    }

    /* */

    resetSort() {
        this.menu.sort = {
            property: '',
            order: 0,
        };
    }

    applySort(applyFor: 'search' | 'courts' | 'users') {
        if (applyFor === 'search') {
            this.loadSearch(this.slider.curr.mode, {
                ...this.slider.curr.query,

                ...this.menu.sort.property && this.menu.sort.order
                    ? {
                        sort: {
                            [this.menu.sort.property]: this.menu.sort.order,
                        },
                    }
                    : {
                        sort: {},
                    },

                page: 1,
            });

            return;
        }

        if (applyFor === 'courts') {
            this.loadCourts(this.slider.curr.id, this.slider.curr.mode, {
                ...this.slider.curr.query,

                ...this.menu.sort.property && this.menu.sort.order
                    ? {
                        sort: {
                            [this.menu.sort.property]: this.menu.sort.order,
                        },
                    }
                    : {
                        sort: {},
                    },

                page: 1,
            });

            return;
        }

        if (applyFor === 'users') {
            this.loadUsers(this.slider.curr.id, this.slider.curr.mode, {
                ...this.slider.curr.query,

                ...this.menu.sort.property && this.menu.sort.order
                    ? {
                        sort: {
                            [this.menu.sort.property]: this.menu.sort.order,
                        },
                    }
                    : {
                        sort: {},
                    },

                page: 1,
            });

            return;
        }
    }
}
